import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  name: string;
  fullScreen?: boolean;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[error-boundary:${this.props.name}]`, error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    if (this.props.fullScreen) {
      return (
        <div className="error-boundary error-boundary--full" role="alert">
          <span className="error-boundary__title">{this.props.name} crashed</span>
          <span className="error-boundary__detail">{error.message}</span>
          <button
            type="button"
            className="error-boundary__btn"
            onClick={this.handleReload}
          >
            Reload App
          </button>
        </div>
      );
    }

    return (
      <div className="error-boundary" role="alert">
        <span className="error-boundary__title">{this.props.name} unavailable</span>
        <span className="error-boundary__detail">{error.message}</span>
        <button
          type="button"
          className="error-boundary__btn"
          onClick={this.handleRetry}
        >
          Retry
        </button>
      </div>
    );
  }
}
